import { useEffect, useRef } from "react";
import { useAuth } from "context/AuthContext";
import { giaoDichApi } from "api/giaoDich";
import { dangKyUnlockAmThanh, phatTinhTinh } from "lib/amThanhThongBao";

const VAI_KT = new Set(["ke_toan", "admin"]);
/** Chỉ nhân viên thu mua — admin/KT không nghe chuông “đã nhận / đã TT” (tránh kêu 2 bên cùng máy). */
const VAI_NV = new Set(["nhan_vien"]);

/** Sau snapshot đầu: im lặng thêm một lúc (tránh reload / snapshot kép Firebase). */
const THOI_CHO_SAU_SNAP_DAU_MS = 2500;
/** Chỉ kêu chuông nếu thời điểm chuông còn “mới” (tránh reload nghe lại chuông cũ). */
const CHUONG_MOI_TRONG_MS = 30_000;

function chuongConMoi(iso) {
  const t = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(t)) return false;
  return Date.now() - t < CHUONG_MOI_TRONG_MS;
}

function logLoiAmThanh(nguon, err) {
  // eslint-disable-next-line no-console
  console.error(`[am-thanh/${nguon}]`, err?.message || err);
}

/**
 * Realtime âm thanh:
 * - KT/admin: chỉ 2 lần — phiếu mới cho_ke_toan (thông báo), hoặc NV bấm chuông nhắc
 * - NV: chuông khi KT nhận thông tin (dang_thanh_toan) hoặc TT xong (da_thanh_toan) — chỉ máy NV
 * Dùng listener nhẹ (không ganTen) để tránh đốt quota Firestore.
 * Không kêu khi F5/reload (skip snapshot đầu + khoảng im lặng + chuông phải còn mới).
 */
export default function useAmThanhThanhToan() {
  const { user, vaiTro, loading } = useAuth();
  const skipChoRef = useRef(true);
  const skipNvRef = useRef(true);
  const imLangChoRef = useRef(true);
  const imLangNvRef = useRef(true);
  const idsChoKeToanRef = useRef(new Set());
  const chuongRef = useRef(new Map());
  const trangThaiNvRef = useRef(new Map());

  useEffect(() => dangKyUnlockAmThanh(), []);

  useEffect(() => {
    if (loading || !user || !VAI_KT.has(vaiTro)) return undefined;
    skipChoRef.current = true;
    imLangChoRef.current = true;
    idsChoKeToanRef.current = new Set();
    chuongRef.current = new Map();
    const uid = user.uid;
    let moAmThanhTimer = null;

    const huy = giaoDichApi.langNgheTrangThaiHangChoKeToan(
      (ds) => {
        const moiCho = (ds || []).filter((po) => po.trangThaiThanhToan === "cho_ke_toan");
        const idsMoi = new Set(moiCho.map((po) => po.id));
        const chuongMoi = new Map();
        moiCho.forEach((po) => {
          if (po.thoiGianChuongKeToan) chuongMoi.set(po.id, po.thoiGianChuongKeToan);
        });

        if (skipChoRef.current) {
          skipChoRef.current = false;
          idsChoKeToanRef.current = idsMoi;
          chuongRef.current = chuongMoi;
          moAmThanhTimer = setTimeout(() => {
            imLangChoRef.current = false;
          }, THOI_CHO_SAU_SNAP_DAU_MS);
          return;
        }

        // Đang trong cửa sổ im lặng sau F5 — chỉ cập nhật baseline, không kêu.
        if (imLangChoRef.current) {
          idsChoKeToanRef.current = idsMoi;
          chuongRef.current = chuongMoi;
          return;
        }

        let coYeuCauMoi = false;
        moiCho.forEach((po) => {
          if (!idsChoKeToanRef.current.has(po.id) && po.maNhanVien !== uid) {
            coYeuCauMoi = true;
          }
        });

        let coChuong = false;
        moiCho.forEach((po) => {
          const tg = po.thoiGianChuongKeToan;
          if (!tg) return;
          if (chuongRef.current.get(po.id) === tg) return;
          if (po.maNhanVien === uid) return;
          if (!chuongConMoi(tg)) return;
          coChuong = true;
        });

        idsChoKeToanRef.current = idsMoi;
        chuongRef.current = chuongMoi;

        if (coChuong) phatTinhTinh("chuong");
        else if (coYeuCauMoi) phatTinhTinh("yeu_cau");
      },
      (err) => logLoiAmThanh("hang-cho-kt", err)
    );

    return () => {
      if (moAmThanhTimer) clearTimeout(moAmThanhTimer);
      huy();
    };
  }, [loading, user, vaiTro]);

  useEffect(() => {
    if (loading || !user || !VAI_NV.has(vaiTro)) return undefined;
    skipNvRef.current = true;
    imLangNvRef.current = true;
    trangThaiNvRef.current = new Map();
    let moAmThanhTimer = null;

    const huy = giaoDichApi.langNgheTrangThaiCuaToi(
      user.uid,
      (ds) => {
        const mapMoi = new Map();
        (ds || []).forEach((po) => {
          mapMoi.set(po.id, po.trangThaiThanhToan);
        });
        if (skipNvRef.current) {
          skipNvRef.current = false;
          trangThaiNvRef.current = mapMoi;
          moAmThanhTimer = setTimeout(() => {
            imLangNvRef.current = false;
          }, THOI_CHO_SAU_SNAP_DAU_MS);
          return;
        }
        if (imLangNvRef.current) {
          trangThaiNvRef.current = mapMoi;
          return;
        }
        let nhan = false;
        let daTt = false;
        mapMoi.forEach((tt, id) => {
          const cu = trangThaiNvRef.current.get(id);
          if (cu === tt) return;
          if (tt === "dang_thanh_toan" && cu !== "dang_thanh_toan") nhan = true;
          if (tt === "da_thanh_toan" && cu !== "da_thanh_toan") daTt = true;
        });
        trangThaiNvRef.current = mapMoi;
        // Chỉ máy NV: KT nhận hoặc TT xong → chuông (máy KT im).
        if (daTt || nhan) phatTinhTinh("chuong");
      },
      (err) => logLoiAmThanh("cua-toi-nv", err)
    );

    return () => {
      if (moAmThanhTimer) clearTimeout(moAmThanhTimer);
      huy();
    };
  }, [loading, user, vaiTro]);
}
